// eslint-disable-next-line no-unused-vars
import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContextSelector } from 'use-context-selector';
import { useForm } from 'react-hook-form';

import ModalManager from 'Containers/ModalManager/ModalManager';
import { WishlistContext } from 'Contexts/WishlistContext';
import EditWishlistModal from 'Components/Wishlists/EditWishlistModal';
import { ModalsContext } from 'Contexts/ModalsContext';
import useAnalytics from 'Hooks/useAnalytics';

const defaultValues = {
  name: '',
  isDefault: false,
};

const CreateWishlistModal = memo(() => {
  const analytics = useAnalytics();
  const { wishlists, createList } = useContextSelector(
    WishlistContext,
    data => ({
      createList: data.createList,
      wishlists: data.data
    })
  );
  const modalsContext = useContextSelector(
    ModalsContext,
    data => data
  );
  const {
    open: modalOpen,
    moveProductsFromCart = false,
    moveProductsFromCartCallback = () => {}
  } = modalsContext.modalState.CREATE_WISHLIST.state;
  const onCloseModal = () => modalsContext.setState(modalsContext.modalState.CREATE_WISHLIST.key, {
    open: false,
    moveProductsFromCart: false,
    moveProductsFromCartCallback: null
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const {
    formState: { errors: rhfErrors },
    register,
    reset,
    getValues,
    setValue,
    trigger,
  } = useForm({
    defaultValues,
    mode: 'onChange'
  });

  const required = t('errors.fieldBlank');
  const minLength = value => ({ value, message: t('errors.fieldMinLength', { minLength: value }) });
  const maxLength = value => ({ value, message: t('errors.fieldMaxLength', { maxLength: value }) });

  const validators = {
    name: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
      validate: {
        duplicate: value => {
          if (!value) return true;
          const normalizedValue = value.trim().toLowerCase();
          const duplicateExists = Object.values(wishlists).find(list => list.name.trim().toLowerCase() === normalizedValue);
          if (normalizedValue === 'stock alerts' || duplicateExists) {
            return t('wishlists.errors.sameName');
          }
          return true;
        }
      }
    },
    isDefault: {}
  };

  const handleOnClose = () => {
    analytics.track('closed_create_wishlist_modal');
    if (!loading) {
      onCloseModal();
      setLoading(false);
      reset(defaultValues);
    }
  };

  const handleOnSubmit = async e => {
    e.preventDefault();
    analytics.track('created_wishlist');

    await trigger();

    const rhfErrorsKeys = Object.keys(rhfErrors);
    if (rhfErrorsKeys.length) {
      // Stop execution if any errors
      return;
    }

    try {
      setLoading(true);
      setStatus(null);
      const formData = getValues();
      const listData = await createList(formData);
      if (moveProductsFromCart) {
        moveProductsFromCartCallback(listData);
      }
      setStatus(true);
      setTimeout(() => {
        setStatus(null);
        onCloseModal();
        reset(defaultValues);
      }, 250);
    } catch (error) {
      setStatus(false);
    } finally {
      setLoading(false);
    }
  };

  const handleOnFocus = e => {
    setTimeout(() => {
      e.target.focus();
      e.target.parentNode.focus();
      e.target.parentNode.parentNode.focus();
    }, 150);
  };

  return (
    <ModalManager open={ modalOpen }>
      <EditWishlistModal
        onFocus={ handleOnFocus }
        className="CreateWishlistModal"
        loading={ loading }
        title={ t('wishlists.createList') }
        status={ status }
        onClose={ handleOnClose }
        onSubmit={ handleOnSubmit }
        validators={ validators }
        register={ register }
        rhfErrors={ rhfErrors }
        setValue={ setValue }
      />
    </ModalManager>
  );
});

CreateWishlistModal.displayName = 'CreateWishlistModalContainer';

export default CreateWishlistModal;
